import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { StyleSheet } from 'react-native';
import { Avatar } from 'react-native-paper';

import { AccountBalanceIcon } from '@/assets';
import { MediaItemImage } from '@/features/media/components/MediaItemImage';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { circleSize } from '@/shared/utils/circle';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { SVGComponent } from '@/types';

type Props = {
  mediaItem?: ClientMediaItem;
  size?: number;
  fallbackIcon?: SVGComponent;
};

/**
 * Displays an organization logo as a circular image, falling back to an icon
 * when no logo is set. Used in detail screens and list rows.
 */
export const OrganizationLogo: FC<Props> = ({
  mediaItem,
  size = 40,
  fallbackIcon: FallbackIcon = AccountBalanceIcon
}) => {
  const theme = useAppTheme();

  if (mediaItem) {
    return (
      <MediaItemImage
        mediaItem={mediaItem}
        mediaSize={MediaItemSize.THUMBNAIL}
        style={[circleSize(size), styles.overflowHidden]}
      />
    );
  }

  return (
    <Avatar.Icon
      size={size}
      style={{ backgroundColor: theme.colors.primaryContainer }}
      icon={() => (
        <FallbackIcon
          fill={theme.colors.onPrimaryContainer}
          width={Math.round(size * 0.6)}
          height={Math.round(size * 0.6)}
        />
      )}
    />
  );
};

const styles = StyleSheet.create({
  overflowHidden: { overflow: 'hidden' }
});

/**
 * Returns an SVGComponent-compatible function that renders the organization
 * logo, for use as the `icon` prop of `ListItem`.
 *
 * Falls back to `fallbackIcon` (default: AccountBalanceIcon) when no logo
 * is available, in which case the original SVG component is returned directly
 * so that ListItem's fill/width/height props are applied normally.
 */
export const organizationLogoIcon = (
  mediaItem?: ClientMediaItem,
  fallbackIcon: SVGComponent = AccountBalanceIcon
): SVGComponent => {
  if (!mediaItem) return fallbackIcon;
  const LogoAsIcon: SVGComponent = () => (
    <OrganizationLogo
      mediaItem={mediaItem}
      size={24}
      fallbackIcon={fallbackIcon}
    />
  );
  return LogoAsIcon;
};
